import React, { useRef, useCallback, useState } from 'react';
import { useLingui } from '@lingui/react';
import classNames from 'classnames';
import { Alert, Tooltip } from 'antd';
import { useMutation } from '@apollo/client';

import { getNetworkError } from 'util/utilFunctions';
import { UPDATE_CANDIDATE_PROFILE_PICTURE } from 'api/candidate_api';
import { CANDIDATE_PROFILE_DETAILS_FRAGMENT } from 'api/fragments';
import { MutationUpdateCandidatePictureArgs, Mutation } from 'types';
import { trackEvent } from 'analytics';
import { FETCH_CANDIDATE_PUBLIC_PROFILE } from 'api/public_profile_api';

// Max size is 5MB
const MEGABYTE = Math.pow(1024, 2);
const MAX_FILE_SIZE = MEGABYTE * 5;

const PROFILE_PIC_TOOLTIP =
  'Your photo will be used to personalize your invitations to your references and will be shown on your Searchlight profile if you choose to make that public.';

export interface ProfilePicProps {
  candidateFirstName: string;
  canEdit?: boolean;
  candidatePhoto?: string;
  size?: number;
}

const ProfilePic = ({
  candidatePhoto,
  candidateFirstName,
  canEdit = false,
  size = 20,
}: ProfilePicProps): JSX.Element => {
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState('');
  const [error, setError] = useState('');
  const profileRef = useRef<HTMLDivElement>() as React.MutableRefObject<HTMLDivElement>;
  const fileReaderRef = useRef(new FileReader());
  const [uploadProfile] = useMutation<
    Pick<Mutation, 'updateCandidatePicture'>,
    MutationUpdateCandidatePictureArgs
  >(UPDATE_CANDIDATE_PROFILE_PICTURE);
  const uploadProfileCallback = useCallback(
    async (candidatePhoto: string) => {
      try {
        // Track when a user tries to upload profile pic from the side bar in the new experience
        trackEvent('Candidate Experience Side Bar Profile Pic Uploaded');
        await uploadProfile({
          variables: {
            input: {
              candidatePhoto,
            },
          },
          update: (cache, { data }) => {
            const id = data?.updateCandidatePicture.candidateProfileId;
            cache.writeFragment({
              id: `CandidateProfile_${id}`,
              fragment: CANDIDATE_PROFILE_DETAILS_FRAGMENT,
              data: {
                candidatePhoto: data?.updateCandidatePicture.candidatePhoto,
              },
            });
          },
          refetchQueries: [FETCH_CANDIDATE_PUBLIC_PROFILE],
          awaitRefetchQueries: true,
        });
      } catch (e) {
        return e;
      }
    },
    [uploadProfile]
  );

  // Wrap the provided callback with a tracking event
  const onFileChange = useCallback(
    e => {
      if (loading) {
        return;
      }
      setLoading(true);
      if (e.target.files && e.target.files[0]) {
        const file = e.target.files[0];
        if (file.size > MAX_FILE_SIZE) {
          setError(`File must be less than ${MAX_FILE_SIZE / MEGABYTE}MB.`);
          // Need to reset the value of the input field to protect against users
          // who might try to reupload the same file
          setProfile('');
          return;
        }
        const { current: FR } = fileReaderRef;
        FR.addEventListener('load', async e => {
          const img: HTMLImageElement = profileRef.current.querySelector('img')!;

          img.src = e.target!.result as string;
          setProfile('');
          const error = await uploadProfileCallback(e.target!.result as string);
          if (error) setError(getNetworkError(error));
        });

        FR.readAsDataURL(e.target.files[0]);
        setLoading(false);
      }
    },
    [loading, uploadProfileCallback]
  );
  const onDismiss = useCallback(() => setError(''), []);
  const { i18n } = useLingui();

  return (
    <>
      {error && (
        <Alert
          className="upload-error"
          type="error"
          message={i18n._(/*i18n*/ { id: error, message: error })}
          onClose={onDismiss}
          closable
        />
      )}
      <div ref={profileRef}>
        {canEdit && (
          <input
            type="file"
            accept=".gif,.jpg,.png"
            name="file"
            id="file"
            className="tw-w-0 tw-h-0 tw-opacity-0 tw-overflow-hidden tw-absolute tw-z-[1]"
            onChange={onFileChange}
            value={profile}
          />
        )}
        <label
          htmlFor="file"
          className={classNames(
            'tw-relative tw-text-white tw-text-center tw-self-end tw-justify-self-end',
            { 'tw-cursor-pointer': canEdit }
          )}
        >
          <img
            id="profile"
            alt={`${candidateFirstName}'s profile`}
            className={classNames(
              `tw-object-cover tw-w-${size} tw-h-${size} tw-aspect-square tw-rounded-full`,
              {
                'tw-invisible tw-absolute': !candidatePhoto,
              }
            )}
            src={candidatePhoto}
          />
          {!candidatePhoto && canEdit && (
            <>
              <Tooltip title={PROFILE_PIC_TOOLTIP} placement="topRight">
                <div
                  className={classNames(
                    `tw-aspect-square tw-w-${size} tw-h-${size} tw-rounded-full tw-bg-gray-200 tw-flex tw-items-center tw-justify-center tw-text-2xl tw-text-gray`,
                    {
                      'tw-text-2xl': size >= 20,
                      'tw-text-base': size <= 10,
                    }
                  )}
                >
                  +
                </div>
              </Tooltip>
            </>
          )}
        </label>
      </div>
    </>
  );
};

export default ProfilePic;
